import { SlideUpdate } from 'api/slides';
import { CaseUpdate } from 'api/study';
import { Procedure } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';
import { first, map, pick, size } from 'lodash';
import { QueryParamConfig, StringParam } from 'use-query-params';
import { DEFAULT_PAGE_SIZE } from 'utils/useEncodedFilters/constants';

import { createIndividualCasesForSlides } from '../ProceduresPage/usePendingSlides';
import type { CaseOrganizationExecutionMode } from './caseOrganizationPlan';
import { OrganizationEntryContext } from './organizationTypes';

export type ManifestSurface =
  | 'Upload Manifest'
  | 'Manifest'
  | 'Not Ingested Slides'
  | 'Lab Lost and Found Slides'
  | 'Study Lost and Found Slides';

export const ManifestSurfaceParam: QueryParamConfig<ManifestSurface | undefined> = StringParam as QueryParamConfig<
  ManifestSurface | undefined
>;

export type PendingSlidesSelectionAction = 'groupSelectedSlidesIntoSingleCase' | 'assignSelectedSlidesToOwnCases';

export type ManifestCaseTarget = number | 'new' | null;

export type SlideAdditionChoice =
  | { kind: 'individualCases' }
  | { kind: 'singleCase' }
  | { kind: 'assignManually' }
  | { kind: 'existingCase'; caseId: number };

export interface SlideAdditionOptions {
  continueAdding: boolean;
}

export interface ManifestDraftIssue {
  field: string;
  message: string;
  rawValue?: string;
}

export interface ManifestDraftCase extends Procedure {
  draftIssues?: ManifestDraftIssue[];
  draftIdentifier?: { slideId: string | null; fileName: string | null };
  draftTarget?: ManifestCaseTarget;
}

export interface ManifestDialogUrlState {
  organizationEntry?: OrganizationEntryContext;
  draftCases?: ManifestDraftCase[];
  draftWarnings?: string[];
  operationErrors?: string[];
  manualManifestSlideIdsToLoad?: string[];
  manualManifestGroupIntoSingleCase?: boolean;
  existingCaseAssignmentsBySlideId?: Record<string, ManifestCaseTarget>;
  manualManifestErrorMessages?: string[];
  removedSlideIds?: string[];
  casePatchesById?: Record<number, Partial<CaseUpdate>>;
  slidePatchesById?: Record<string, Partial<SlideUpdate>>;
  executionMode?: CaseOrganizationExecutionMode;
  showOnlyErrors?: boolean;
  showOnlyUnassignedSlides?: boolean;
  useFileNameForNewCaseLabels?: boolean;
  page?: number;
  rowsPerPage?: number;
  pendingSlidesSelectionAction?: PendingSlidesSelectionAction;
  reviewUnassignedSlidesWhenAvailable?: boolean;
  reviewExistingCasesWhenAvailable?: boolean;
  includeStudyPendingSlidesWhenAvailable?: boolean;
}

export const getDefaultManifestDialogUrlState = (): ManifestDialogUrlState => ({
  organizationEntry: undefined,
  draftCases: [],
  draftWarnings: [],
  operationErrors: [],
  manualManifestSlideIdsToLoad: [],
  manualManifestGroupIntoSingleCase: false,
  existingCaseAssignmentsBySlideId: {},
  manualManifestErrorMessages: [],
  removedSlideIds: [],
  casePatchesById: {},
  slidePatchesById: {},
  showOnlyErrors: false,
  showOnlyUnassignedSlides: false,
  useFileNameForNewCaseLabels: true,
  page: 1,
  rowsPerPage: DEFAULT_PAGE_SIZE,
  pendingSlidesSelectionAction: undefined,
  reviewUnassignedSlidesWhenAvailable: undefined,
  reviewExistingCasesWhenAvailable: undefined,
  includeStudyPendingSlidesWhenAvailable: undefined,
});

export const normalizeManifestDialogState = (state?: ManifestDialogUrlState | null): ManifestDialogUrlState => ({
  ...getDefaultManifestDialogUrlState(),
  ...state,
});

export const assignManifestDraftCaseIds = ({
  cases,
  retainedCases,
  casePatchesById = {},
}: {
  cases: Procedure[];
  retainedCases: Procedure[];
  casePatchesById?: Record<number, Partial<CaseUpdate>>;
}): Procedure[] => {
  let nextDraftCaseId = 0;
  for (const procedure of retainedCases) {
    nextDraftCaseId = Math.min(nextDraftCaseId, procedure.id);
  }
  for (const caseId of Object.keys(casePatchesById)) {
    nextDraftCaseId = Math.min(nextDraftCaseId, Number(caseId));
  }
  return map(cases, (procedure) => {
    nextDraftCaseId -= 1;
    return { ...procedure, id: nextDraftCaseId };
  });
};

const DEFAULT_PENDING_SLIDES_CASE_ID = 0;
const DEFAULT_CANCER_TYPE_ID = 9;

const caseOrganizationSlideFieldKeys = [
  'id',
  'studyId',
  'sourceStudyId',
  'labId',
  'createdAt',
  'originalFileName',
  'biopsySiteId',
  'stainingType',
  'biopsyType',
  'preservationMethod',
  'procedureType',
  'positiveControl',
  'negativeControl',
  'metastasis',
] as const;

type CaseOrganizationSlidePayload = Pick<Slide, (typeof caseOrganizationSlideFieldKeys)[number]>;

const buildCaseOrganizationSlidePayload = (slide: Slide): CaseOrganizationSlidePayload =>
  pick(slide, caseOrganizationSlideFieldKeys);

export const buildCaseOrganizationRequestPayload = ({
  procedures,
  studyId,
}: {
  procedures: ManifestDraftCase[];
  studyId: string;
}): Procedure[] => {
  return map(
    procedures,
    ({ draftIssues: _issues, draftIdentifier: _identifier, draftTarget: _target, ...procedure }) => ({
      ...procedure,
      studyId,
      slideIds: map(procedure.slides, 'id'),
      slides: map(procedure.slides, buildCaseOrganizationSlidePayload),
    })
  );
};

export const buildSingleCaseManifestDraft = ({
  slides,
  labId,
  caseLabel,
}: {
  slides: Slide[];
  labId: string;
  caseLabel?: string;
}): Procedure[] => {
  const firstSlide = first(slides);

  if (!firstSlide) {
    return [];
  }

  const resolvedCaseLabel = caseLabel ?? (size(slides) === 1 ? firstSlide.originalFileName || '' : '');

  return [
    {
      id: DEFAULT_PENDING_SLIDES_CASE_ID,
      isPendingSlideMockCase: true,
      labId,
      name: resolvedCaseLabel,
      label: resolvedCaseLabel,
      batchDate: undefined,
      createdAt: firstSlide.createdAt,
      diagnosis: undefined,
      cancerTypeId: DEFAULT_CANCER_TYPE_ID,
      cancerSubtypes: [],
      clinicalData: undefined,
      internallyApproved: undefined,
      features: undefined,
      studyId: firstSlide.sourceStudyId || firstSlide.studyId,
      slides: map(slides, (slide) => ({ ...slide })),
    } as Procedure,
  ];
};

export const buildOwnCaseManifestDraft = ({ slides, labId }: { slides: Slide[]; labId: string }): Procedure[] =>
  createIndividualCasesForSlides(slides, labId);

// Unassigned slides keep an empty label and no target case so the case assignment flags both for the user to fill in.
export const buildUnassignedCaseManifestDraft = ({ slides, labId }: { slides: Slide[]; labId: string }): Procedure[] =>
  map(createIndividualCasesForSlides(slides, labId), (procedure) => ({ ...procedure, name: '', label: '' }));
